/**
 * Workflow analytics: one schema for every user input flow, on every page, so
 * the same action (e.g. annotating stops) can be compared across the realtime
 * dashboard and the databases page and stitched into larger user workflows.
 *
 * A workflow is:
 *   - started   → the user opens the input (modal, inline editor)
 *   - completed → the request resolves, with `success` reflecting the backend
 *   - abandoned → the input is closed without a completed request
 *
 * Inline edits that commit on change (no open/close step) only emit
 * `completed`.
 */

export enum EWorkflow {
  AnnotateStops = 'annotate_stops',
  SplitStop = 'split_stop',
  UpdateStopSettings = 'update_stop_settings',
  AssignOperator = 'assign_operator',
  SplitOperatorAssignment = 'split_operator_assignment',
  AssignSku = 'assign_sku',
  LogProduction = 'log_production',
  EditProduction = 'edit_production',
  SplitProduction = 'split_production',
  DeleteProduction = 'delete_production',
  ReviewProduction = 'review_production',
  LogDefects = 'log_defects',
  EditDefects = 'edit_defects',
  DeleteDefects = 'delete_defects',
  CreateCustomField = 'create_custom_field',
  EditCustomField = 'edit_custom_field',
  DeleteCustomField = 'delete_custom_field',
  ExportData = 'export_data'
}

export type ViewMode = 'realtime' | 'historical';

export type ExportFormat = 'csv' | 'async';

/** Shared context: who is acting, where, and on which machine. */
export type WorkflowContextBase = {
  machine_id?: number;
  machine_name?: string;
  company_id?: number;
  company_name?: string;
  view_mode?: ViewMode;
  /** Databases tab the flow ran from. */
  tab?: string;
};

/**
 * Flow-specific details, known once the user acts on a row or submits. Values
 * passed to `trackStarted` carry over to the `completed`/`abandoned` events.
 */
export type WorkflowDetails = Pick<
  WorkflowContextBase,
  'machine_id' | 'machine_name'
> & {
  /** The single field an inline edit changed. */
  field?: string;
  /** Fields a multi-field edit changed. */
  changed_fields?: string[];
  /** Rows, stops or split ranges the flow acted on. */
  items_count?: number;
  /** Whether the stop had no annotation before this one. */
  is_first_annotation?: boolean;
  /** Annotation overwrite confirmed after a concurrent-edit conflict. */
  is_forced?: boolean;
  /** Review flows: the state the row is moved to. */
  is_reviewed?: boolean;
  /** Custom field flows: the field's data type. */
  field_type?: string;
  export_format?: ExportFormat;
  export_type?: string;
  days_span?: number;
  columns_count?: number;
  recipients_count?: number;
};

export type WorkflowContext = WorkflowContextBase & {
  workflow_type: EWorkflow;
};

export type WorkflowEventProperties = WorkflowContext & WorkflowDetails;

export type WorkflowStartedProperties = WorkflowEventProperties & {
  timestamp: string;
};

export type WorkflowCompletedProperties = WorkflowEventProperties & {
  success: boolean;
  /** Time from the submit press to the backend response. */
  submit_duration_ms?: number;
};

/** The event names a page emits its workflows under. */
export type WorkflowEventNames = {
  started: string;
  /**
   * Optional: `completed` carries the same information plus the outcome
   * (FE-844), so new pages omit it.
   */
  submitted?: string;
  completed: string;
  abandoned: string;
};
